'use client'

import { useState, useMemo, useCallback } from 'react'

export interface ViewportCenter {
  lat: number
  lng: number
}

export interface ViewportBounds {
  north: number
  south: number
  east: number
  west: number
}

export interface MapState {
  hoveredProductId: string | null
  setHoveredProductId: (id: string | null) => void
  selectedProductId: string | null
  setSelectedProductId: (id: string | null) => void
  viewportCenter: ViewportCenter | null
  setViewportCenter: (center: ViewportCenter | null) => void
  viewportBounds: ViewportBounds | null
  setViewportBounds: (bounds: ViewportBounds | null) => void
  zoom: number | null
  setZoom: (zoom: number | null) => void
  isInteracting: boolean
  setIsInteracting: (interacting: boolean) => void
  clearSelection: () => void
}

interface UseMapStateOptions {
  defaultZoom?: number | null
}

export function useMapState(options: UseMapStateOptions = {}): MapState {
  const { defaultZoom = null } = options

  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [viewportCenter, setViewportCenter] = useState<ViewportCenter | null>(null)
  const [viewportBounds, setViewportBounds] = useState<ViewportBounds | null>(null)
  const [zoom, setZoom] = useState<number | null>(defaultZoom)
  const [isInteracting, setIsInteracting] = useState(false)

  const clearSelection = useCallback(() => {
    setHoveredProductId(null)
    setSelectedProductId(null)
  }, [])

  return useMemo(
    () => ({
      hoveredProductId,
      setHoveredProductId,
      selectedProductId,
      setSelectedProductId,
      viewportCenter,
      setViewportCenter,
      viewportBounds,
      setViewportBounds,
      zoom,
      setZoom,
      isInteracting,
      setIsInteracting,
      clearSelection,
    }),
    [hoveredProductId, selectedProductId, viewportCenter, viewportBounds, zoom, isInteracting, clearSelection]
  )
}
